import * as React from "react";
import { useSelector } from "react-redux";
import lodashGet from "lodash/get";

import Card from "@material-ui/core/Card";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";

import MuiTable from "../../../components/MuiTable";
import CardHeader from "../../../components/CardHeader";
import { useDeepMemo } from "../../../hooks";
import { findSizeBreakupList, getEffFlowInfoList } from "../../../helpers/orderHelper";
import { getStyleColor, getWeightedMean, isEmpty, isTrue, toFixed, toFixedSigned, toFixedValue } from "../../../utils/helpers";
import { InventoryStatus, RollType, SpreadingType } from "../../../utils/types";
import { DEFAULT_SEPARATOR, UOM } from "../../../constants";
import { Divider } from "@material-ui/core";
import { convert } from "../../../helpers/unitConversionHelper";
import { getMarkerList } from "../../cutplan/cutPlanHelper";

const LiveReport = props => {
  const { order, cutOrder, rollList, layRowList } = props;

  const [styleColorTabIdx, setStyleColorTabIdx] = React.useState(0);

  const messages = useSelector(state => state?.i18n?.messages || {});

  const styleColorList = cutOrder?.styleColorList || [];
  const styleColor = isEmpty(styleColorList) ? null : styleColorList[styleColorTabIdx];

  const sizeBreakupList = useDeepMemo(() => findSizeBreakupList(cutOrder, order), [order, cutOrder]);

  const colsCutSummary = useDeepMemo(() => {
    let cols = [
      { dataKey: "parameter", title: "Parameter" },
      ...sizeBreakupList.map(sb => ({ dataKey: sb.sizeKey, title: sb.size, render: value => toFixedValue(value, 0) })),
      { dataKey: "total", title: "Total", render: value => toFixedValue(value, 0) },
      { dataKey: "totalPercent", title: "Total (%)" }
    ];
    return cols;
  }, [sizeBreakupList]);

  const colsFabricUsage = useDeepMemo(() => {
    if (isEmpty(cutOrder)) return [];
    const hasWeight = rollList.some(e => !isEmpty(e.weight) && e.weight > 0);

    let cols = [
      { dataKey: "style", title: "Style" },
      { dataKey: "color", title: "Color" },
      { dataKey: "allocated", title: "Allocated", width: 10 },
      { dataKey: "extra", title: "Extra", width: 9 },
      { dataKey: "issued", title: "Issued", width: 9 },
      { dataKey: "returned", title: "Returned", width: 9 },
      { dataKey: "netIssued", title: "Net Issued", width: 9 },
      { dataKey: "reserved", title: "Reserved", width: 9 },
      { dataKey: "usedInMarker", title: "Used in Marker", width: 9 },
      { dataKey: "endLoss", title: "End Loss", width: 9 },
      { dataKey: "endBits", title: "Net End Bits", width: 9 },
      { dataKey: "usedInPanel", title: "Panel Rep." },
      { dataKey: "damaged", title: "Damaged", width: 9 },
      { dataKey: "missing", title: "Missing", width: 9 },
      { dataKey: "balance", title: "Balance", width: 9 },
      { dataKey: "liveCu", title: "Live CU", width: 9 },
      { dataKey: "shortageExcess", title: "Short/Extra Fab.", width: 10 },
      { dataKey: "expShortQty", title: "Exp. Short Qty.", width: 10 }
    ];
    if (cutOrder.styleList?.length <= 1) {
      cols = cols.filter(c => c.dataKey !== "style");
    }
    if (cutOrder.colorList?.length <= 1) {
      cols = cols.filter(c => c.dataKey !== "color");
    }
    if (cutOrder?.isManualPlan) {
      cols = cols.filter(c => !["extra", "reserved"].includes(c.dataKey));
    }
    if (hasWeight) {
      cols = cols.filter(c => !["shortageExcess", "expShortQty"].includes(c.dataKey));
    }
    return cols;
  }, [cutOrder, rollList]);

  const rowsCutSummaryMap = useDeepMemo(() => {
    if (isEmpty(sizeBreakupList) || isEmpty(cutOrder) || isEmpty(rollList)) return [];
    const sizeKeyList = sizeBreakupList.map(sb => sb.sizeKey);
    const flowInfoList = getEffFlowInfoList(order, cutOrder);
    const emptyQty = sizeKeyList.reduce((acc, sizeKey) => ({ ...acc, [sizeKey]: 0 }), {});

    const approvedQtyMap = !isEmpty(cutOrder?.orderConfig?.approvedOrderCutQty) ? JSON.parse(cutOrder.orderConfig.approvedOrderCutQty) : {};
    // const fabricUsageMap = !isEmpty(cutOrder?.orderConfig?.fabricUsageInfo) ? JSON.parse(cutOrder?.orderConfig?.fabricUsageInfo) : {};

    const finalLayRowList =
      layRowList
        ?.filter(row => isTrue(row.completed))
        ?.map(row => ({
          ...row,
          rollInfo: rollList?.find(r => r.id === row.rollInfoId),
          marker: cutOrder.markerList?.find(m => m.id === row.markerId)
        })).filter(e => !isEmpty(e.marker)) || [];
    const markerList = getMarkerList(cutOrder);
    const markerIds = markerList.map(e => e.id);    

    const rowsMap = flowInfoList.reduce((acc, flowInfo) => {
      const sizeBreakupList = flowInfo.sizeBreakupList.filter(sb => sb.type === "ORDER");
      const orderQty = sizeBreakupList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: sb.qty }), emptyQty);
      const totalOrderQty = sizeBreakupList.reduce((acc, sb) => acc + sb.qty, 0);

      const approvedQty = approvedQtyMap[flowInfo.styleColor] || {};
      const totalApprovedQty = Object.values(approvedQty).reduce((acc, qty) => acc + qty, 0);
      let totalApprovedPercent = totalOrderQty > 0 ? `${toFixedValue((totalApprovedQty * 100) / totalOrderQty, 1)}%` : "";

      // const fabricUsage = fabricUsageMap[flowInfo.styleColor] || {};

      // const extraFabric = rollList
      //   .filter(e => e.styleColor === flowInfo.styleColor && (e.rollType === RollType.EXCESS || e.plannedRollType === RollType.EXCESS))
      //   .reduce((acc, e) => acc + e.length, 0);
      // const rejectedFabric = rollList
      //   .filter(e => e.styleColor === flowInfo.styleColor && e.rollType === RollType.REJECTED)
      //   .reduce((acc, e) => acc + e.available, 0);
      // const missingFabric = rollList
      //   .filter(e => e.styleColor === flowInfo.styleColor && e.rollType === RollType.MISSING)
      //   .reduce((acc, e) => acc + e.available, 0);
      // const shortageExcess = rollList
      //   .filter(e => e.styleColor === flowInfo.styleColor && e.rollType === RollType.NORMAL)
      //   .reduce((acc, e) => acc + (e.shortageExcess || 0), 0);
      // const effFabShortage = Math.abs(Math.min(0, extraFabric - rejectedFabric - missingFabric + shortageExcess));
      // const shortQty =
      // !isEmpty(fabricUsage.actualCu) && Number(fabricUsage.actualCu) > 0 ? Math.floor(effFabShortage / Number(fabricUsage.actualCu)) : 0;

      // const totalSuggestedQty = totalApprovedQty - shortQty;
      // const suggestedFraction = totalSuggestedQty / totalOrderQty;
      // const suggestedQtyMap = flowInfo.fpoSizeBreakupList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: Math.round(sb.qty * suggestedFraction) }), {});

      const cutQty = sizeKeyList.reduce((acc, sizeKey) => {
        const qty = finalLayRowList
          .filter(row => markerIds.includes(row.marker.id))
          .filter(row => row.rollInfo?.styleColor === flowInfo.styleColor)
          .reduce((acc, row) => {
            const sizeRatio = row.marker?.sizeRatioList.find(sr => sr.sizeKey === sizeKey);
            const plies = SpreadingType.FACE_ONE_WAY !== row.marker?.spreadingType ? row.actualPly / 2 : row.actualPly;
            const qty = sizeRatio ? sizeRatio.ratio * plies : 0;
            return acc + qty;
          }, 0);
        return { ...acc, [sizeKey]: qty };
      }, emptyQty);
      const totalCutQty = Object.values(cutQty).reduce((acc, qty) => acc + qty, 0);
      let totalCutPercent = totalOrderQty > 0 ? `${toFixed((totalCutQty * 100) / totalOrderQty, 1)}%` : "";

      const balanceQty = sizeKeyList.reduce(
        (acc, sizeKey) => ({ ...acc, [sizeKey]: toFixedSigned((cutQty[sizeKey] || 0) - (approvedQty[sizeKey] || 0), 0) }),
        {}
      );
      const totalBalanceQty = toFixedSigned(totalCutQty - totalApprovedQty, 0);
      let balancePercent = totalOrderQty > 0 ? `${toFixedValue((totalBalanceQty * 100) / totalOrderQty, 1)}%` : "";

      const fpoQty = flowInfo.fpoSizeBreakupList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: (acc[sb.sizeKey] || 0) + sb.qty }), {});
      let totalFpoQty = Object.keys(fpoQty).reduce((acc, key) => acc + fpoQty[key], 0);
      const fpoPercent = totalOrderQty > 0 ? `${toFixedValue((totalFpoQty * 100) / totalOrderQty, 1)}%` : "";

      // Shortage/Excess Qty
      // const shortageExcessQty = sizeList.reduce((acc, sizeKey) => {
      //   const qty = layRowList
      //     .filter(row => row.rollInfo?.styleColor === flowInfo.styleColor)
      //     .reduce((acc, row) => {
      //       const sizeRatio = row.marker?.sizeRatioList.find(sr => sr.sizeKey === sizeKey);
      //       let plyDiff = row.actualPly - row.plannedPly;
      //       if (CutPlanMarkerType.CLOSED === row.marker?.spreadingType) {
      //         plyDiff = plyDiff / 2;
      //       }
      //       const qty = sizeRatio ? sizeRatio.ratio * plyDiff : 0;
      //       return acc + qty;
      //     }, 0);
      //   return { ...acc, [sizeKey]: qty };
      // }, {});
      // const totalShortageExcessQty = Object.values(shortageExcessQty).reduce((acc, qty) => acc + qty, 0);

      let rows = [
        { parameter: "Order Qty", total: totalOrderQty, totalPercent: `100.0%`, ...orderQty },
        { parameter: lodashGet(messages, "resources.orders.fields.fpoQty", "FPO Qty"), total: totalFpoQty, totalPercent: fpoPercent, ...fpoQty },
        { parameter: "Approved Qty", total: totalApprovedQty, totalPercent: totalApprovedPercent, ...approvedQty },
        // { parameter: "Suggested Qty", total: Object.values(suggestedQtyMap).reduce((acc, e) => acc + e, 0), ...suggestedQtyMap },
        { parameter: "Cut Qty", total: totalCutQty, totalPercent: totalCutPercent, ...cutQty },
        { parameter: "Balance to Cut", total: totalBalanceQty, totalPercent: balancePercent, ...balanceQty }
        // { parameter: "Shortage/Excess", total: totalShortageExcessQty, ...shortageExcessQty }
      ];
      if (totalOrderQty === totalFpoQty) {
        rows.splice(1, 1);
      }
      return { ...acc, [flowInfo.styleColor]: rows };
    }, {});

    return rowsMap;
  }, [sizeBreakupList, cutOrder, layRowList, rollList, messages]);

  const rowsFabricUsage = useDeepMemo(() => {
    if (isEmpty(order) || isEmpty(cutOrder)) return [];
    const flowInfoList = getEffFlowInfoList(order, cutOrder);

    const fabricUsageList = !isEmpty(cutOrder?.orderConfig?.fabricUsageInfo) ? JSON.parse(cutOrder?.orderConfig?.fabricUsageInfo) : [];

    const rowList = flowInfoList.map(flowInfo => {
      const hasWeight = rollList.some(e => !isEmpty(e.weight) && e.weight > 0);

      const fabricUsage = hasWeight
        ? fabricUsageList.find(e => getStyleColor(e.style, e.color) === flowInfo.styleColor && e.uom === UOM.KILOGRAM.value)
        : fabricUsageList.find(e => getStyleColor(e.style, e.color) === flowInfo.styleColor && e.uom === UOM.METER.value);
      const usedRollList = rollList
        .filter(e => e.usedInLay > 0 && e.styleColor === flowInfo.styleColor)
        .map(roll => {
          const part = flowInfo?.partList?.find(p => p.effPartName === cutOrder.part);
          const bomCu = part?.bomCu || 1;
          const mList = cutOrder.markerList?.filter(m => m.group?.id === roll?.finalGroupId) || [];
          const smallestML = mList.sort((a, b) => a.layLength - b.layLength)[0]?.layLength || bomCu;
          return { ...roll, endBits: roll.available < smallestML ? roll.available : 0 };
        });

      const finalLayRowList = Array.isArray(layRowList)
        ? layRowList.map(row => ({
            ...row,
            rollInfo: rollList?.find(r => r.id === row.rollInfoId),
            marker: cutOrder.markerList?.find(m => m.id === row.markerId)
          }))
        : [];

      if (hasWeight) {
        const totalWeight = rollList.filter(e => e?.styleColor === flowInfo.styleColor).reduce((acc, r) => acc + (r.weight || 0), 0);
        const allocated = cutOrder?.isManualPlan ? Number(toFixed(totalWeight, 1)) : Number(toFixed(fabricUsage?.allocated, 1));
        const issued = rollList.filter(ri => ri.invStatus === InventoryStatus.ISSUED && ri?.styleColor === flowInfo.styleColor).reduce((acc, ri) => acc + (ri.weight || 0), 0);
        const returned = rollList
          .filter(e => e?.styleColor === flowInfo.styleColor)
          .filter(ri => ri.returnStatus === "Returned" || ri.returnStatus === "Confirmed")
          .reduce((acc, ri) => acc + (ri.weight || 0), 0);
        const netIssued = issued - returned;
        const missing = rollList
          .filter(e => e?.styleColor === flowInfo.styleColor)
          .filter(ri => (isEmpty(ri.returnStatus) ? ri.rollType === RollType.MISSING : ri.returnStatus === "Missed"))
          .reduce((acc, r) => acc + (r.weight || 0), 0);
        const damaged = rollList.filter(e => e.rollType === RollType.REJECTED && e?.styleColor === flowInfo.styleColor).reduce((acc, r) => acc + (r.availableWeight || 0), 0);

        const usedInLay = usedRollList.reduce((acc, r) => acc + (r.usedInLayWeight || 0), 0);
        const endLoss = finalLayRowList
          .filter(row => row.rollInfo?.styleColor === flowInfo.styleColor)
          .filter(row => isTrue(row.completed) && !isTrue(row.skipped))
          .reduce((acc, row) => acc + (row.actualPly * (row.marker?.endLoss || 0)) / 100, 0);

        const gsmList = rollList.filter(e => e?.styleColor === flowInfo.styleColor).map(e => ({ key: e.gsm, weight: e.weight }));
        const fWidthList = rollList.filter(e => e?.styleColor === flowInfo.styleColor).map(e => ({ key: e.finishedWidth, weight: e.weight }));
        const gsm = getWeightedMean(gsmList);
        const finishedWidth = getWeightedMean(fWidthList);
        const endLossWeight = convert({ input: endLoss, uomIn: UOM.METER.value, uomOut: UOM.KILOGRAM.value, widthInCm: finishedWidth, gsm });

        const usedInMarker = Math.max(0, usedInLay - endLossWeight);
        const endBits = usedRollList.reduce((acc, r) => acc + (r.endBitsWeight || 0), 0);
        const usedInPanel = rollList.filter(e => e.styleColor === flowInfo.styleColor).reduce((acc, r) => acc + (r.usedInPanelWeight || 0), 0);
        const balance = netIssued - usedInLay - endBits - usedInPanel - damaged - missing;

        const totalFabricUsed = usedInLay + endBits + usedInPanel + damaged;
        const rowsCutSummary = rowsCutSummaryMap[flowInfo.styleColor] || [];
        const cutQty = rowsCutSummary.find(e => e.parameter === "Cut Qty")?.total || 0;
        const liveCu = cutQty > 0 ? totalFabricUsed / cutQty : 0;

        return {
          style: flowInfo.style,
          color: flowInfo.color,
          allocated: `${toFixedValue(allocated, 1)} kg`,
          issued: `${toFixedValue(issued, 1)} kg`,
          returned: `${toFixedValue(returned, 1)} kg`,
          netIssued: `${toFixedValue(netIssued, 1)} kg`,
          extra: `${toFixedValue(fabricUsage?.extra, 1)} kg`,
          reserved: `${toFixedValue(fabricUsage?.reserved, 1)} kg`,
          usedInLay: `${toFixedValue(usedInLay, 1)} kg`,
          usedInMarker: `${toFixedValue(usedInMarker, 1)} kg`,
          endLoss: `${toFixedValue(endLossWeight, 1)} kg`,
          endBits: `${toFixedValue(endBits, 1)} kg`,
          usedInPanel: `${toFixedValue(usedInPanel, 1)} kg`,
          liveCu: `${toFixedValue(liveCu, 3)} kg`,
          damaged: `${toFixedValue(damaged, 1)} kg`,
          missing: `${toFixedValue(missing, 1)} kg`,
          balance: `${toFixedValue(balance, 1)} kg`
        };
      } else {
        const totalLength = rollList.filter(e => e?.styleColor === flowInfo.styleColor).reduce((acc, r) => acc + r.length, 0);
        const allocated = cutOrder?.isManualPlan ? Number(toFixed(totalLength, 1)) : Number(toFixed(fabricUsage?.allocated, 1));
        const issued = rollList.filter(ri => ri.invStatus === InventoryStatus.ISSUED && ri?.styleColor === flowInfo.styleColor).reduce((acc, ri) => acc + ri.length, 0);

        const returned = rollList
          .filter(e => e?.styleColor === flowInfo.styleColor)
          .filter(ri => ri.returnStatus === "Returned" || ri.returnStatus === "Confirmed")
          .reduce((acc, ri) => acc + ri.length, 0);
        const netIssued = issued - returned
        const missing = rollList
          .filter(e => e?.styleColor === flowInfo.styleColor)
          .filter(ri => (isEmpty(ri.returnStatus) ? ri.rollType === RollType.MISSING : ri.returnStatus === "Missed"))
          .reduce((acc, r) => acc + r.length, 0);
        const damaged = rollList.filter(e => e.rollType === RollType.REJECTED && e?.styleColor === flowInfo.styleColor).reduce((acc, r) => acc + r.available, 0);

        const usedInLay = usedRollList.reduce((acc, r) => acc + r.usedInLay, 0);
        const endLoss = layRowList
          .filter(row => row.rollInfo?.styleColor === flowInfo.styleColor)
          .filter(row => isTrue(row.completed) && !isTrue(row.skipped))
          .reduce((acc, row) => acc + (row.actualPly * (row?.marker?.endLoss || 0)) / 100, 0);
        const usedInMarker = Math.max(0, usedInLay - endLoss);
        const endBits = usedRollList.reduce((acc, r) => acc + r.endBits, 0);
        const usedInPanel = rollList.filter(e => e.styleColor === flowInfo.styleColor).reduce((acc, r) => acc + (r.usedInPanel || 0), 0);
        const shortageExcess = usedRollList.filter(e => e.styleColor === flowInfo.styleColor).reduce((acc, r) => acc + (r.shortageExcess || 0), 0);
        const balance = netIssued + shortageExcess - usedInLay - endBits - usedInPanel - damaged - missing;

        const totalFabricUsed = usedInLay + endBits + usedInPanel + damaged;
        const rowsCutSummary = rowsCutSummaryMap[flowInfo.styleColor] || [];
        const cutQty = rowsCutSummary.find(e => e.parameter === "Cut Qty")?.total || 0;
        const liveCu = cutQty > 0 ? totalFabricUsed / cutQty : 0;

        const extraFabric = rollList
          .filter(e => e.styleColor === flowInfo.styleColor && (e.rollType === RollType.EXCESS || e.plannedRollType === RollType.EXCESS))
          .reduce((acc, e) => acc + e.length, 0);
        const effFabShortage = Math.abs(Math.min(0, extraFabric - damaged - missing + shortageExcess));
        const expShortQty =
          !isEmpty(fabricUsage?.actualCu) && Number(fabricUsage?.actualCu) > 0 ? Math.floor(-effFabShortage / Number(fabricUsage?.actualCu)) : 0;

        return {
          style: flowInfo.style,
          color: flowInfo.color,
          allocated: `${toFixedValue(allocated, 1)} m`,
          issued: `${toFixedValue(issued, 1)} m`,
          returned: `${toFixedValue(returned, 1)} m`,
          netIssued: `${toFixedValue(netIssued, 1)} m`,
          extra: `${toFixedValue(fabricUsage?.extra, 1)} m`,
          reserved: `${toFixedValue(fabricUsage?.reserved, 1)} m`,
          usedInLay: `${toFixedValue(usedInLay, 1)} m`,
          usedInMarker: `${toFixedValue(usedInMarker, 1)} m`,
          endLoss: `${toFixedValue(endLoss, 1)} m`,
          endBits: `${toFixedValue(endBits, 1)} m`,
          usedInPanel: `${toFixedValue(usedInPanel, 1)} m`,
          liveCu: `${toFixedValue(liveCu, 3)} m`,
          damaged: `${toFixedValue(damaged, 1)} m`,
          missing: `${toFixedValue(missing, 1)} m`,
          balance: `${toFixedValue(balance, 1)} m`,
          shortageExcess: toFixedSigned(shortageExcess, 1),
          expShortQty: toFixedSigned(expShortQty, 0)
        };
      }
    });

    return rowList;
  }, [order, cutOrder, rollList, layRowList, rowsCutSummaryMap]);

  const rowsCutSummary = useDeepMemo(() => {
    if (isEmpty(styleColor) || isEmpty(rowsCutSummaryMap)) return [];

    return rowsCutSummaryMap[styleColor];
  }, [styleColor, rowsCutSummaryMap]);

  return (
    <div>
      <Card style={{ margin: "1rem" }}>
        <CardHeader title="Fabric Usage" />

        <MuiTable columns={colsFabricUsage} rows={rowsFabricUsage} i18nKey="resources.layslip.fields.fabricUsage" />
      </Card>

      <Card style={{ margin: "1rem" }}>
        {styleColorList.length > 1 && (
          <Tabs value={styleColorTabIdx} onChange={(e, idx) => setStyleColorTabIdx(idx)} variant="scrollable" scrollButtons="auto">
            {styleColorList.map((styleColor, idx) => (
              <Tab style={{ maxWidth: 320 }} key={idx} label={`${styleColor.split(DEFAULT_SEPARATOR).join(", ")}`} />
            ))}
          </Tabs>
        )}
        <Divider />
        <MuiTable columns={colsCutSummary} rows={rowsCutSummary} />
      </Card>
    </div>
  );
};

export default LiveReport;
